import { useOnboardUserMutation } from "@/features/onboarding/api/onboardingApi";
import { useListOrganizationsQuery } from "@/features/organizations/api/organizationsApi";
import { getProblem } from "@/shared/api/errors";
import { handleMutationError } from "@/shared/api/handleMutationError";
import { notify } from "@/shared/components/notify";
import { zodResolver } from "@/shared/forms/zodResolver";
import { roleSelectOptions } from "@/shared/i18n/roleOptions";
import { type OnboardingLanguage, OnboardingLanguageSchema } from "@keystone/contracts/onboarding";
import { ErrorCode } from "@keystone/contracts/shared";
import { type AssignableRole, AssignableRoleSchema } from "@keystone/contracts/users";
import { Button, Group, Modal, Select, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const LANGUAGE_KEYS: Record<OnboardingLanguage, string> = {
  en: "languages.en",
  "fr-CA": "languages.fr",
  "es-419": "languages.es",
};

const FormSchema = z.object({
  email: z.email(),
  orgId: z.string(),
  role: z.union([AssignableRoleSchema, z.literal("")]),
  language: OnboardingLanguageSchema,
});

interface OnboardUserModalProps {
  opened: boolean;
  onClose: () => void;
}

interface FormValues {
  email: string;
  orgId: string;
  role: AssignableRole | "";
  language: OnboardingLanguage;
}

export function OnboardUserModal({ opened, onClose }: OnboardUserModalProps) {
  const { t } = useTranslation("onboarding");
  const { data: orgs, isLoading: orgsLoading } = useListOrganizationsQuery();
  const [onboardUser, { isLoading }] = useOnboardUserMutation();

  const orgOptions = useMemo(
    () => (orgs?.items ?? []).map((o) => ({ value: o.id, label: o.name })),
    [orgs],
  );

  const languageOptions = useMemo(
    () =>
      OnboardingLanguageSchema.options.map((code) => ({
        value: code,
        label: t(LANGUAGE_KEYS[code]),
      })),
    [t],
  );

  const form = useForm<FormValues>({
    initialValues: { email: "", orgId: "", role: "", language: "en" },
    validate: zodResolver(FormSchema),
    validateInputOnBlur: true,
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = form.onSubmit(async (values) => {
    if (values.orgId && !values.role) {
      form.setFieldError("role", t("form.errors.roleRequiredForClient"));
      return;
    }
    try {
      await onboardUser({
        email: values.email,
        language: values.language,
        ...(values.orgId ? { orgId: values.orgId } : {}),
        ...(values.role ? { role: values.role } : {}),
      }).unwrap();
      notify.success("onboarding:notifications.onboarded");
      handleClose();
    } catch (e) {
      const problem = getProblem(e as Parameters<typeof getProblem>[0]);
      if (problem?.code === ErrorCode.UserAlreadyExists) {
        form.setFieldError("email", t("form.errors.emailTaken"));
      } else {
        handleMutationError(e, "onboarding:errors.failed");
      }
    }
  });

  return (
    <Modal opened={opened} onClose={handleClose} title={t("modal.title")} centered>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="sm">
          <TextInput
            label={t("form.email")}
            placeholder={t("form.emailPlaceholder")}
            required
            type="email"
            {...form.getInputProps("email")}
          />
          <Select
            label={t("form.organization")}
            placeholder={t("form.organizationPlaceholder")}
            data={orgOptions}
            value={form.values.orgId || null}
            onChange={(value) => {
              form.setFieldValue("orgId", value ?? "");
              form.clearFieldError("role");
            }}
            error={form.errors.orgId}
            searchable
            clearable
            disabled={orgsLoading}
            nothingFoundMessage={t("form.noOrgsAvailable")}
          />
          <Select
            label={form.values.orgId ? t("form.role") : t("form.roleOptional")}
            placeholder={form.values.orgId ? t("form.rolePlaceholder") : t("form.noRole")}
            data={roleSelectOptions(AssignableRoleSchema.options, t)}
            value={form.values.role || null}
            onChange={(value) => form.setFieldValue("role", (value as AssignableRole) ?? "")}
            error={form.errors.role}
            searchable
            clearable
            required={Boolean(form.values.orgId)}
          />
          <Select
            label={t("form.language")}
            data={languageOptions}
            value={form.values.language}
            onChange={(value) =>
              form.setFieldValue("language", (value as OnboardingLanguage) ?? "en")
            }
            error={form.errors.language}
            allowDeselect={false}
            required
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={handleClose} type="button">
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading}>
              {t("modal.submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
